import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { SettingService } from '@services/setting.service';
import { HOME_SEARCH_STATE_KEY } from '@models/GlobalSearch/globalSearch.model';

// One clearable group of browser-saved settings. `keys` are localStorage keys
// written by the page(s) named in `label`, or sessionStorage keys (kept for this
// browser tab only) when `session` is set.
export interface CachedSettingsGroup {
  label: string;
  description: string;
  keys: string[];
  session?: boolean;
}

@Component({
  standalone: false,
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {

  // The panel is a troubleshooting tool (a stale saved search or page state can
  // stop a page from loading), so it stays collapsed until asked for.
  settingsPanelExpanded = false;

  // Every localStorage key the app itself writes, grouped by the page that owns
  // it. Clearing is always by explicit key -- never localStorage.clear() -- because
  // MSAL keeps the auth tokens in the same localStorage (cacheLocation:
  // 'localStorage' in src/settings/settings*.ts) and wiping those signs the user
  // out mid-session. Add new keys here when a page starts persisting state.
  readonly cachedSettingsGroups: CachedSettingsGroup[] = [
    {
      label: 'Gateways',
      description: 'Saved search text',
      keys: ['GatewaySearchBox']
    },
    {
      label: 'Sensors',
      description: 'Saved search text',
      keys: ['SensorSearchBox']
    },
    {
      label: 'Sensor Groups',
      description: 'Saved search text',
      keys: ['SensorGroupSearchBox']
    },
    {
      label: 'Events & Actions',
      description: 'Saved search text, selected columns, column widths, evaluation details options',
      keys: ['TriggerSearchBox', 'triggerTable_selectedColumns', 'triggerTable_columnWidths', 'TriggerEvalDetailsOptions']
    },
    {
      label: 'Action Queues',
      description: 'Saved search text',
      keys: ['ActionQueueSearchBox']
    },
    {
      label: 'Analyze Sensor Readings',
      description: 'Last-used sensors, reading types, dates, chart settings, active tab',
      keys: ['ReviewReadingsLastUsed', 'ReviewReadingsActiveTab']
    },
    {
      label: 'Chart Group Readings',
      description: 'Last-used sensor groups, dates, and chart settings',
      keys: ['SensorReadingsLastUsed']
    },
    {
      label: 'GeoMap Readings',
      description: 'Last-used sensors, dates, and map settings',
      keys: ['MapReadingsLastUsed']
    },
    {
      label: 'Dashboards',
      description: 'Favorites tile size, last selected dashboard, sidebar state',
      // DashboardImageFavoritesLayout is no longer written (one Favorites tile
      // size now); kept so a stale value left in a browser still gets cleared.
      keys: ['DashboardFavoritesLayout', 'DashboardImageFavoritesLayout', 'DashboardLastSelected', 'DashboardSidebarCollapsed']
    },
    {
      label: 'Scoutview Image Favorites',
      description: 'Selected image favorites and image size',
      keys: ['ScoutviewImageFavoritesSelected', 'ScoutviewImageFavoritesLayout']
    },
    {
      label: 'Shared Display Settings',
      description: 'Timezone, original/calibrated values, chart dark mode, chart colors (used by several pages)',
      keys: ['TimeZoneSelected', 'ShowOriginalReadings', 'ShowCalibratedReadings', 'EchartDarkBackground', 'EchartColorScheme']
    },
    {
      label: 'Home Search',
      description: 'Last search text and results (this browser tab only)',
      keys: [HOME_SEARCH_STATE_KEY],
      session: true
    }
  ];

  // Link for the User Documentation tile, from the Settings table. Stays empty
  // (tile shown disabled) if the setting has not been created or cannot be read.
  userDocumentationUrl = '';

  constructor(private toastr: ToastrService,
              private confirmationService: ConfirmationService,
              private settingService: SettingService) { }

  ngOnInit(): void {
    this.settingService.getUserDocumentationUrl().subscribe({
      next: url => this.userDocumentationUrl = (url ?? '').trim(),
      error: () => this.userDocumentationUrl = ''
    });
  }


  toggleSettingsPanel(): void {
    this.settingsPanelExpanded = !this.settingsPanelExpanded;
  }


  // True when at least one of the group's keys is currently present, so the row
  // can show whether there is anything to clear.
  hasSavedSettings(group: CachedSettingsGroup): boolean {
    const storage = this.storageFor(group);
    return group.keys.some(key => storage?.getItem(key) != undefined);
  }


  // Where the group's keys are kept. Null if the browser blocks that storage.
  private storageFor(group: CachedSettingsGroup): Storage | null {
    try {
      return group.session ? sessionStorage : localStorage;
    } catch {
      return null;
    }
  }


  get totalSavedGroups(): number {
    return this.cachedSettingsGroups.filter(g => this.hasSavedSettings(g)).length;
  }


  // No confirmation: only cached preferences are lost, and the user is here
  // because a page will not load.
  clearSettingsGroup(group: CachedSettingsGroup): void {
    const storage = this.storageFor(group);
    const cleared = group.keys.filter(key => storage?.getItem(key) != undefined);
    cleared.forEach(key => storage?.removeItem(key));

    if (cleared.length > 0) {
      this.toastr.success('Cleared the saved settings for ' + group.label);
    }
    else {
      this.toastr.info('There were no saved settings for ' + group.label);
    }
  }


  clearAllSettingsConfirm(): void {
    this.confirmationService.confirm({
      message: 'Please confirm that you want to clear the saved settings for every page. '
             + 'Your saved searches and last-used page selections will be reset. '
             + 'You will stay signed in.',
      key: 'clearSettings',
      header: 'Clear All Saved Page Settings',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.clearAllSettings();
      },
      reject: () => {
      }
    });
  }


  private clearAllSettings(): void {
    var clearedCount = 0;
    this.cachedSettingsGroups.forEach(group => {
      const storage = this.storageFor(group);
      group.keys.forEach(key => {
        if (storage?.getItem(key) != undefined) {
          storage.removeItem(key);
          clearedCount++;
        }
      });
    });

    if (clearedCount > 0) {
      this.toastr.success('Cleared ' + clearedCount + ' saved settings');
    }
    else {
      this.toastr.info('There were no saved settings to clear');
    }
  }

}
